import { useCallback, useEffect, useRef, useState } from "react";
import * as Switch from "@radix-ui/react-switch";
import type { ImportResult } from "@parafon/contracts/local-api";
import { DepolamaKarti } from "../components/BulutYedegiKarti";
import { KilitKarti } from "../components/kilit/KilitKarti";
import { SutunYerlesimi } from "../components/SutunYerlesimi";
import { AbonelikKarti } from "../kabiliyet/AbonelikKarti";
import { ImportReport } from "../components/ImportReport";
import { decryptPfai, encryptPfai, readPfaiHeader } from "../lib/pfai";
import { downloadBlob, readFileAsText } from "../lib/files";
import { formatBytes } from "../lib/format";
import { useTheme } from "../components/ThemeProvider";
import {
  NAKIT_TAKIBI_AYARI,
  NAKIT_TAKIBI_SECENEKLERI,
  nakitTakibiDurumu,
  type NakitTakibiDurumu,
  type NakitTakibiSecimi,
} from "../lib/nakitTakibi";
import type { ThemeChoice } from "../components/ThemeProvider";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useAsync, useMutation } from "../repo/useAsync";
import { Button, Field, InlineError, LoadingBlock, Notice, Screen, Select, Spinner } from "../ui/primitives";
import { DIL, DILLER, M, YEREL, dilDegistir, type DilKodu } from "../dil";
import { ELLE_YEDEK_AYARI, elleYedekOzeti } from "../lib/elleYedek";
import { KaliciDepolamaUyarisi } from "../components/KaliciDepolamaUyarisi";
import { kartSetiniKaydet, kayitliKartSeti, type KartSeti } from "../lib/kartSeti";
import { ICI_ZEMIN_AYARI, MENU_ZEMINI_AYARI, useZeminAyarlari } from "../lib/zeminAyarlari";

export function SettingsScreen() {
  const { repo, info, isFake, revision, invalidate } = useRepositoryContext();
  const { choice, setChoice, resolved } = useTheme();

  const exportRun = useMutation();
  const importRun = useMutation();
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // --- şifreli yedek ---
  const pfaiExportRun = useMutation();
  const pfaiImportRun = useMutation();
  const [pfaiParola, setPfaiParola] = useState("");
  const [pfaiParolaTekrar, setPfaiParolaTekrar] = useState("");
  const [pfaiGeriParola, setPfaiGeriParola] = useState("");
  const [pfaiSonuc, setPfaiSonuc] = useState<ImportResult | null>(null);
  const [pfaiBilgi, setPfaiBilgi] = useState<string | null>(null);
  const pfaiFileRef = useRef<HTMLInputElement | null>(null);

  const onPfaiExport = async (): Promise<void> => {
    setPfaiBilgi(null);
    // İKİ KEZ SORULUYOR VE SEBEBİ TEK: yazım hatası olan bir parola, dosyayı
    // GERİ DÖNÜŞSÜZ biçimde açılamaz yapar. Arka kapı yok, kurtarma yok —
    // o yüzden hata yapma imkânını dosya üretilmeden ÖNCE kapatıyoruz.
    if (pfaiParola !== pfaiParolaTekrar) {
      await pfaiExportRun.run(() => {
        throw new Error(M.ayarlar.pfai.parolalarFarkli);
      });
      return;
    }
    const ok = await pfaiExportRun.run(async () => {
      const json = await repo.exportPlainJson();
      const dosya = await encryptPfai(json, pfaiParola);
      downloadBlob(
        new Blob([dosya], { type: "application/octet-stream" }),
        `parafon-yedek-${new Date().toISOString().slice(0, 10)}.pfai`,
      );
      // Kalıcı depolama uyarısı için: bu yedekteki kasanın özeti, kasanın
      // kendi (şifreli) ayarlarında (gelen karar 02.11.2026).
      await repo.settings.set(ELLE_YEDEK_AYARI, await elleYedekOzeti(json));
    });
    if (ok) {
      // PAROLA BELLEKTE TUTULMUYOR: alanlar temizleniyor. Ekranda kalan bir
      // parola, açık bırakılmış bir sekmede okunabilir bir sırdır.
      setPfaiParola("");
      setPfaiParolaTekrar("");
      setPfaiBilgi(M.ayarlar.pfai.indirildi);
      invalidate();
    }
  };

  const onPfaiImport = async (file: File): Promise<void> => {
    setPfaiSonuc(null);
    setPfaiBilgi(null);
    const ok = await pfaiImportRun.run(async () => {
      const metin = await readFileAsText(file);
      // Başlık ÇÖZMEDEN okunuyor: yanlış dosya seçildiyse kullanıcı parola
      // denemeden önce anlıyor ve 600.000 turluk bekleme boşa gitmiyor.
      const baslik = await readPfaiHeader(metin);
      const json = await decryptPfai(metin, pfaiGeriParola);
      const sonuc = await repo.importPlainJson(json);
      setPfaiSonuc(sonuc);
      setPfaiBilgi(M.ayarlar.pfai.yedekTarihi(new Date(baslik.createdAt).toLocaleString(YEREL)));
    });
    if (ok) {
      setPfaiGeriParola("");
      invalidate();
    }
    if (pfaiFileRef.current !== null) pfaiFileRef.current.value = "";
  };

  const settingsLoad = useCallback(() => repo.settings.all(), [repo]);
  const settingsState = useAsync(settingsLoad, [repo, revision]);


  const onExport = async (): Promise<void> => {
    await exportRun.run(async () => {
      const json = await repo.exportPlainJson();
      const blob = new Blob([json], { type: "application/json" });
      downloadBlob(blob, `parafon-yedek-${new Date().toISOString().slice(0, 10)}.json`);
    });
  };

  const onImportFile = async (file: File): Promise<void> => {
    setImportResult(null);
    const ok = await importRun.run(async () => {
      const text = await readFileAsText(file);
      const result = await repo.importPlainJson(text);
      setImportResult(result);
    });
    if (ok) invalidate();
    if (fileRef.current !== null) fileRef.current.value = "";
  };

  return (
    <Screen title={M.ayarlar.ekran.baslik} description={M.ayarlar.ekran.aciklama}>
      <div className="grid gap-4 p-4">
        <div className="mx-auto grid w-full max-w-[53rem] gap-4">
        {/* ------------------------------------------------------------------
            KALICI UYARI — kullanıcıya süslemeden söylenmesi gerekenler.
           ------------------------------------------------------------------ */}
        <KaliciDepolamaUyarisi />

        {isFake && (
          <Notice tone="danger" title={M.ayarlar.sahte.baslik}>
            <p>{M.ayarlar.sahte.metin}</p>
          </Notice>
        )}
        </div>

        {/* Kartlar ekrana sığdığı kadar sütunda (Gelen kararlar 01.11.2026). */}
        <SutunYerlesimi>
        {/* --- kilitli defter: kilit, süre, parola, kurtarma ifadesi --- */}
        <KilitKarti />

        {/* --- Depolama: yalnızca bu cihazda / bulut depolama (19.10.2026) --- */}
        <DepolamaKarti />

        {/* --- Abonelik: ücretli kabiliyetler ve aktivasyon kodu (gelir modeli) --- */}
        <AbonelikKarti />

        {/* --- tema --- */}
        <section className="pf-card p-4" data-kart-bolum="gorunum">
          <h2 className="text-[14px] font-semibold text-ink">{M.ayarlar.gorunum.baslik}</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field
              label={M.ayarlar.gorunum.tema}
              htmlFor="set-theme"
              hint={M.ayarlar.gorunum.uygulanan(resolved === "dark" ? M.ayarlar.gorunum.koyuKucuk : M.ayarlar.gorunum.acikKucuk)}
            >
              <Select
                id="set-theme"
                value={choice}
                onChange={(e) => setChoice(e.currentTarget.value as ThemeChoice)}
                options={[
                  { value: "system", label: M.ayarlar.gorunum.sistem },
                  { value: "light", label: M.ayarlar.gorunum.acik },
                  { value: "dark", label: M.ayarlar.gorunum.koyu },
                ]}
              />
            </Field>

            <div className="flex items-center gap-3">
              <Switch.Root
                id="set-dark"
                checked={resolved === "dark"}
                onCheckedChange={(on) => setChoice(on ? "dark" : "light")}
                className="relative h-5 w-9 shrink-0 rounded-full border border-line-strong bg-surface-3 data-[state=checked]:bg-accent"
              >
                <Switch.Thumb className="block size-3.5 translate-x-0.5 rounded-full bg-surface transition-transform data-[state=checked]:translate-x-[1.15rem]" />
              </Switch.Root>
              <label htmlFor="set-dark" className="text-[13px] text-ink">
                {M.ayarlar.gorunum.koyuTema}
              </label>
            </div>

            {/* Dil: her dil kendi adıyla (sözlüğe girmez); değişince sayfa yeniden yüklenir. */}
            <Field label={M.ayarlar.gorunum.dil} htmlFor="set-dil" hint={M.ayarlar.gorunum.dilAciklama}>
              <Select
                id="set-dil"
                value={DIL}
                onChange={(e) => dilDegistir(e.currentTarget.value as DilKodu)}
                options={DILLER.map((d) => ({ value: d.kod, label: d.ad }))}
              />
            </Field>
            <KartSetiSecimi />
          </div>
          <ZeminAnahtarlari />
        </section>

        <NakitTakibiKarti />

        {/* --- depo bilgisi --- */}
        <section className="pf-card p-4" data-kart-bolum="veri">
          <h2 className="text-[14px] font-semibold text-ink">{M.ayarlar.depo.baslik}</h2>
          <dl className="mt-3 grid gap-x-6 gap-y-2 text-[13px]">
            <InfoRow term="VFS" value={info.vfs} />
            <InfoRow term={M.ayarlar.depo.semaSurumu} value={String(info.schemaVersion)} numeric />
            <InfoRow
              term={M.ayarlar.depo.kaliciDepolama}
              value={info.persistent ? M.ayarlar.depo.evet : M.ayarlar.depo.hayir}
              tone={info.persistent ? "gain" : "warn"}
            />
            <InfoRow term={M.ayarlar.depo.tahminiKota} value={formatBytes(info.estimatedQuotaBytes)} numeric />
            <InfoRow term={M.ayarlar.depo.cihazKimligi} value={info.deviceId} numeric />
          </dl>

          <h3 className="mt-4 text-[13px] font-semibold text-ink">{M.ayarlar.depo.kayitliAyarlar}</h3>
          {settingsState.state.status === "loading" && settingsState.state.data === undefined ? (
            <LoadingBlock label={M.ayarlar.depo.okunuyor} />
          ) : settingsState.state.status === "error" ? (
            <p className="mt-1 text-[12px] text-danger">{M.ayarlar.depo.okunamadi}</p>
          ) : Object.keys(settingsState.state.data ?? {}).length === 0 ? (
            <p className="mt-1 text-[12px] text-faint">{M.ayarlar.depo.bos}</p>
          ) : (
            <dl className="mt-2 grid gap-x-6 gap-y-1 text-[12px]">
              {Object.entries(settingsState.state.data ?? {}).map(([k, v]) => (
                <InfoRow key={k} term={k} value={v} numeric />
              ))}
            </dl>
          )}
        </section>

        {/* --- ŞİFRELİ YEDEK: önerilen yol --- */}
        <section className="pf-card p-4" data-kart-bolum="veri">
          <h2 className="text-[14px] font-semibold text-ink">{M.ayarlar.pfai.baslik}</h2>
          <p className="mt-1 text-[13px] text-muted">
            {M.ayarlar.pfai.p1OnEk}
            <strong>{M.ayarlar.pfai.p1Kalin}</strong>
            {M.ayarlar.pfai.p1SonEk}
          </p>
          <p className="mt-2 text-[13px] text-muted">
            {M.ayarlar.pfai.p2OnEk}
            <strong>{M.ayarlar.pfai.p2Kalin1}</strong>
            {M.ayarlar.pfai.p2Orta}
            <strong>{M.ayarlar.pfai.p2Kalin2}</strong>
            {M.ayarlar.pfai.p2SonEk}
          </p>

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <Field label={M.ayarlar.pfai.parola} htmlFor="set-pfai-parola">
              <input
                id="set-pfai-parola"
                type="password"
                autoComplete="new-password"
                className="pf-input"
                value={pfaiParola}
                onChange={(e) => setPfaiParola(e.currentTarget.value)}
              />
            </Field>
            <Field label={M.ayarlar.pfai.parolaTekrar} htmlFor="set-pfai-parola-tekrar">
              <input
                id="set-pfai-parola-tekrar"
                type="password"
                autoComplete="new-password"
                className="pf-input"
                value={pfaiParolaTekrar}
                onChange={(e) => setPfaiParolaTekrar(e.currentTarget.value)}
              />
            </Field>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              onClick={() => void onPfaiExport()}
              disabled={pfaiExportRun.pending || pfaiParola.length === 0}
            >
              {pfaiExportRun.pending ? M.ayarlar.pfai.sifreleniyorUc : M.ayarlar.pfai.indir}
            </Button>
            {pfaiExportRun.pending && <Spinner label={M.ayarlar.pfai.sifreleniyor} />}
          </div>
          <InlineError error={pfaiExportRun.error} />

          <hr className="my-4 border-line" />

          <h3 className="text-[13px] font-semibold text-ink">{M.ayarlar.pfai.geriBaslik}</h3>
          <p className="mt-1 text-[13px] text-muted">
            {M.ayarlar.pfai.g1OnEk}
            <strong>{M.ayarlar.pfai.g1Kalin1}</strong>
            {M.ayarlar.pfai.g1Orta}
            <strong>{M.ayarlar.pfai.g1Kalin2}</strong>
            {M.ayarlar.pfai.g1SonEk}
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <Field label={M.ayarlar.pfai.yedekParolasi}>
              <input
                type="password"
                autoComplete="current-password"
                className="pf-input"
                value={pfaiGeriParola}
                onChange={(e) => setPfaiGeriParola(e.currentTarget.value)}
              />
            </Field>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label className="pf-btn pf-btn-ghost cursor-pointer">
              {M.ayarlar.pfai.dosyaSec}
              <input
                ref={pfaiFileRef}
                type="file"
                accept=".pfai"
                className="sr-only"
                disabled={pfaiGeriParola.length === 0}
                onChange={(e) => {
                  const file = e.currentTarget.files?.[0];
                  if (file) void onPfaiImport(file);
                }}
              />
            </label>
            {pfaiImportRun.pending && <Spinner label={M.ayarlar.pfai.cozuluyor} />}
          </div>
          <InlineError error={pfaiImportRun.error} />
          {pfaiBilgi !== null && (
            <p className="mt-2 text-[13px] text-muted">{pfaiBilgi}</p>
          )}
          {pfaiSonuc !== null && <ImportReport result={pfaiSonuc} title={M.ayarlar.pfai.geriTamam} />}
        </section>

        {/* --- veri çıkış / giriş --- */}
        <section className="pf-card p-4" data-kart-bolum="veri">
          <h2 className="text-[14px] font-semibold text-ink">{M.ayarlar.json.baslik}</h2>
          <p className="mt-1 text-[13px] text-muted">
            {M.ayarlar.json.onEk}
            <code>.pfai</code>
            {M.ayarlar.json.sonEk}
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button variant="primary" onClick={() => void onExport()} disabled={exportRun.pending}>
              {exportRun.pending ? M.ayarlar.json.hazirlaniyor : M.ayarlar.json.indir}
            </Button>

            <label className="pf-btn pf-btn-ghost cursor-pointer">
              {M.ayarlar.json.yukle}
              <input
                ref={fileRef}
                type="file"
                accept="application/json,.json"
                className="sr-only"
                onChange={(e) => {
                  const file = e.currentTarget.files?.[0];
                  if (file) void onImportFile(file);
                }}
              />
            </label>

            {importRun.pending && <Spinner label={M.ayarlar.json.yukleniyor} />}
          </div>

          <InlineError error={exportRun.error} />
          <InlineError error={importRun.error} />

          {importResult !== null && <ImportReport result={importResult} title={M.ayarlar.json.tamam} />}
        </section>
        </SutunYerlesimi>
      </div>
    </Screen>
  );
}

/**
 * NAKİT TAKİBİ — kasa düzeyinde (kullanıcı kararı 14.10.2026). Kapalıyken
 * Varlık Dökümü'nde net nakit hareketi, nakit tablosu ve nakit uyarıları
 * görünmez; temettü/faiz/masraf getiriye yine girer.
 */
function NakitTakibiKarti() {
  const { repo, revision, invalidate } = useRepositoryContext();
  const [durum, setDurum] = useState<NakitTakibiDurumu | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  useEffect(() => {
    let iptal = false;
    void nakitTakibiDurumu(repo).then(
      (d) => {
        if (!iptal) setDurum(d);
      },
      () => {
        if (!iptal) setHata(M.ayarlar.nakit.okunamadi);
      },
    );
    return () => {
      iptal = true;
    };
  }, [repo, revision]);

  const sec = async (secim: NakitTakibiSecimi) => {
    setHata(null);
    try {
      await repo.settings.set(NAKIT_TAKIBI_AYARI, secim);
      setDurum(await nakitTakibiDurumu(repo));
      invalidate();
    } catch {
      setHata(M.ayarlar.nakit.kaydedilemedi);
    }
  };

  return (
    <section className="pf-card p-4" data-kart-bolum="gorunum" aria-label={M.ayarlar.nakit.baslik}>
      <h2 className="text-[14px] font-semibold text-ink">{M.ayarlar.nakit.baslik}</h2>
      <p className="mt-1 text-[12px] text-muted">
        {M.ayarlar.nakit.aciklama}
      </p>
      {durum === null ? (
        hata === null ? (
          <Spinner label={M.ayarlar.nakit.okunuyor} />
        ) : (
          <p className="mt-2 text-[12px] text-danger">{hata}</p>
        )
      ) : (
        <div className="mt-3 sm:w-96">
          <Field
            label={M.ayarlar.nakit.baslik}
            htmlFor="set-nakit"
            hint={M.ayarlar.nakit.suAn(durum.acik ? M.ayarlar.nakit.acikKucuk : M.ayarlar.nakit.kapaliKucuk)}
            {...(hata === null ? {} : { error: hata })}
          >
            <Select
              id="set-nakit"
              value={durum.secim}
              options={NAKIT_TAKIBI_SECENEKLERI.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(e) => void sec(e.currentTarget.value as NakitTakibiSecimi)}
            />
          </Field>
        </div>
      )}
    </section>
  );
}

function InfoRow({
  term,
  value,
  numeric = false,
  tone,
}: {
  readonly term: string;
  readonly value: string;
  readonly numeric?: boolean;
  readonly tone?: "gain" | "warn";
}) {
  const toneClass = tone === "gain" ? "text-gain" : tone === "warn" ? "text-warn" : "text-ink";
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-line py-1 last:border-0">
      <dt className="text-muted">{term}</dt>
      <dd className={`${numeric ? "pf-num" : ""} ${toneClass} break-all text-right`}>{value}</dd>
    </div>
  );
}

/** Zemin anahtarları (02.11.2026): ikisi de varsayılan açık; seçim kasada (eşitlemeyle öbür cihazlara gider). */
function ZeminAnahtarlari() {
  const { repo, revision, invalidate } = useRepositoryContext();
  const zemin = useZeminAyarlari(repo, revision);
  const [hata, setHata] = useState(false);
  const yaz = async (ayar: string, acik: boolean): Promise<void> => {
    try {
      await repo.settings.set(ayar, acik ? "1" : "0");
      setHata(false);
      invalidate();
    } catch {
      setHata(true);
    }
  };
  const satirlar = [
    { id: "set-menu-zemini", ayar: MENU_ZEMINI_AYARI, acik: zemin.menu, etiket: M.ayarlar.gorunum.menuZemini },
    { id: "set-ici-zemin", ayar: ICI_ZEMIN_AYARI, acik: zemin.ici, etiket: M.ayarlar.gorunum.iciZemin },
  ];
  return (
    <div className="mt-4 space-y-2">
      {satirlar.map((r) => (
        <div key={r.id} className="flex items-center gap-3">
          <Switch.Root
            id={r.id}
            checked={r.acik}
            onCheckedChange={(on) => void yaz(r.ayar, on)}
            className="relative h-5 w-9 shrink-0 rounded-full border border-line-strong bg-surface-3 data-[state=checked]:bg-accent"
          >
            <Switch.Thumb className="block size-3.5 translate-x-0.5 rounded-full bg-surface transition-transform data-[state=checked]:translate-x-[1.15rem]" />
          </Switch.Root>
          <label htmlFor={r.id} className="text-[13px] text-ink">
            {r.etiket}
          </label>
        </div>
      ))}
      <p className="text-[12px] text-muted">{M.ayarlar.gorunum.zeminAciklama}</p>
      {hata && <p className="text-[12px] text-danger">{M.ayarlar.nakit.kaydedilemedi}</p>}
    </div>
  );
}

/** Varlık Dökümü kart seti (gelen karar 02.11.2026): yalnızca bu cihazda. */
function KartSetiSecimi() {
  const [kartSeti, setKartSeti] = useState<KartSeti>(kayitliKartSeti);
  return (
    <Field label={M.ayarlar.gorunum.kartSeti} htmlFor="set-kart-seti" hint={M.ayarlar.gorunum.kartSetiAciklama}>
      <Select
        id="set-kart-seti"
        value={kartSeti}
        onChange={(e) => {
          const k = e.currentTarget.value as KartSeti;
          kartSetiniKaydet(k);
          setKartSeti(k);
        }}
        options={[
          { value: "temel", label: M.ayarlar.gorunum.kartSetiTemel },
          { value: "ayrintili", label: M.ayarlar.gorunum.kartSetiAyrintili },
        ]}
      />
    </Field>
  );
}
